
import React from 'react';
import { Button } from '@/components/ui/button';
import { BANNER_SIZES, BannerSize, BannerTextLine } from '@/types/banner';

interface BannerPreviewProps {
  size: BannerSize;
  text?: string;
  textLines?: BannerTextLine[];
  imageUrl?: string | null;
  backgroundColor: string;
  textColor?: string;
  currentAttempt?: number;
  maxAttempts?: number;
  bannerId?: string;
  onGenerateNewVersion?: () => void;
  onApprove?: () => void;
  borderStyle?: string;
  borderWidth?: number;
  borderColor?: string;
  imageSettings?: {
    width: number;
    opacity: number;
    position: { x: number; y: number };
    isAnimated?: boolean;
  };
  onImagePositionChange?: (position: { x: number; y: number }) => void;
  onTextPositionChange?: (index: number, position: { x: number; y: number }) => void;
}

const BannerPreview: React.FC<BannerPreviewProps> = ({
  size,
  text,
  textLines,
  imageUrl,
  backgroundColor,
  textColor = '#000000',
  currentAttempt,
  maxAttempts,
  bannerId,
  onGenerateNewVersion,
  onApprove,
  borderStyle,
  borderWidth,
  borderColor,
  imageSettings,
  onImagePositionChange,
  onTextPositionChange
}) => {
  const sizeConfig = BANNER_SIZES.find(s => s.value === size) || BANNER_SIZES[0];
  
  const style = {
    width: `${sizeConfig.width}px`,
    height: `${sizeConfig.height}px`,
    backgroundColor,
    color: textColor,
    maxWidth: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px',
    boxSizing: 'border-box' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    overflow: 'hidden',
    textAlign: 'center' as const,
    fontSize: sizeConfig.width > 300 ? '18px' : '14px',
    position: 'relative' as const,
    border: borderStyle && borderStyle !== 'none' ? `${borderWidth || 1}px ${borderStyle} ${borderColor || '#000000'}` : 'none',
  };
  
  const containerStyle = {
    overflow: 'auto',
    padding: '8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    backgroundColor: '#f8fafc'
  };
  
  const imageStyle = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover' as const,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    opacity: imageSettings?.opacity !== undefined ? imageSettings.opacity / 100 : 1,
  };

  // Debug information to help diagnose image rendering issues
  React.useEffect(() => {
    if (imageUrl) {
      console.log("BannerPreview - Image URL provided:", imageUrl.substring(0, 100) + "...");
    } else {
      console.log("BannerPreview - No image URL provided");
    }
  }, [imageUrl]);

  // Function to render text content
  const renderTextContent = () => {
    // If textLines is provided, render each line according to its settings
    if (textLines && textLines.length > 0) {
      return textLines.map((line, index) => {
        if (!line.text) return null;
        
        const textStyle = {
          fontFamily: line.fontFamily || 'Arial',
          fontSize: `${line.fontSize || 16}px`,
          color: line.color || textColor,
          fontWeight: line.bold ? 'bold' : 'normal',
          fontStyle: line.italic ? 'italic' : 'normal',
          position: 'relative' as const,
          display: 'block',
          margin: '4px 0',
          zIndex: 2,
        };
        
        return <p key={index} style={textStyle}>{line.text}</p>;
      });
    }
    
    // Fallback to simple text if no textLines provided
    return text && <p style={{ position: 'relative', zIndex: 2 }}>{text}</p>;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error("Error loading banner image:", e);
    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjI1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIGxvYWQgZmFpbGVkPC90ZXh0Pjwvc3ZnPg==';
  };

  return (
    <div style={containerStyle}>
      <div style={style}>
        {imageUrl && (
          <img 
            src={imageUrl} 
            alt="Banner background" 
            style={imageStyle}
            onLoad={() => console.log("Banner image loaded successfully")} 
            onError={handleImageError}
          />
        )}
        <div style={{ position: 'relative', zIndex: 2 }}>
          {renderTextContent()}
        </div>
      </div>
      
      {currentAttempt && maxAttempts && (
        <div className="text-xs text-gray-500 mt-2 text-center">
          Version {currentAttempt} of {maxAttempts}
        </div>
      )}
      
      {(onGenerateNewVersion || onApprove) && (
        <div className="flex justify-center space-x-2 mt-3">
          {onGenerateNewVersion && (
            <Button size="sm" variant="outline" onClick={onGenerateNewVersion}>
              Generate New Version
            </Button>
          )}
          {onApprove && (
            <Button size="sm" onClick={onApprove}>
              Use This Banner
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default BannerPreview;
